import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, getVariableName, tokens, Tag, Text, Stack, Columns, Column, Inline, ButtonIcon, Icon } from '@happy-design/react';
import { useTranslate } from '../../lib/translate.js';
import { useExperienceSwitcher } from './ExperienceSwitcherContext.js';
import { HappyAssetSVG, HappyPropertySVG, HappyForceSVG } from './Graphics.js';

/**
 * FIXME: Package dependencies should be moved, document set up for consumers.
 */ const ProductLineIconBox = styled(Box)`
  width: 2rem;
  height: 2rem;
`;
const NAV_BETA_COLOR_VAR = "var(--hd-unified-nav-beta-color)";
const NAV_BETA_COLOR_BG_VAR = "var(--hd-unified-nav-beta-bg-color)";
const ProductLineWrapper = styled(Box)`
  ${getVariableName(NAV_BETA_COLOR_VAR)}: ${tokens.color.palette.green[140]};
  ${getVariableName(NAV_BETA_COLOR_BG_VAR)}: ${tokens.color.palette.green[10]};

  padding: 1.2rem 0.7rem 1.2rem 0.7rem;
  border: 1px solid transparent;

  & label {
    cursor: pointer;
  }
  &.product-line-expanded {
    padding-bottom: 1rem;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid ${tokens.color.palette.gray[10]};
  }
  &.product-line-expanded {
    padding-bottom: 1rem;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid ${tokens.color.palette.gray[10]};
  }
  &:hover {
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid ${tokens.color.palette.gray[10]};
  }
  &.HAPPY_ASSET {
    ${getVariableName(tokens.color.accent.default)}: var(--hd-unified-nav-asset-primary);
    ${getVariableName(tokens.color.accent.weak)}: var(--hd-unified-nav-asset-weak);
  }
  &.HAPPY_PROPERTY {
    ${getVariableName(tokens.color.accent.default)}: var(--hd-unified-nav-property-primary);
    ${getVariableName(tokens.color.accent.weak)}: var(--hd-unified-nav-property-weak);
  }
  &.HAPPY_FORCE {
    ${getVariableName(tokens.color.accent.default)}: var(--hd-unified-nav-force-primary);
    ${getVariableName(tokens.color.accent.weak)}: var(--hd-unified-nav-force-weak);
  }

  & .product-line-expand,
  & .product-expand {
    padding: 0.75rem;
  }

  & .link-icon,
  & .product-line-expand {
    display: none;
  }
  & .link-icon svg {
    stroke: ${tokens.color.palette.gray[80]};
    color: ${tokens.color.palette.gray[80]};
    stroke-width: 1.5;
  }
  &:hover .link-icon,
  &:hover .product-line-expand,
  &.product-line-expanded .product-line-expand {
    display: inline-flex;
  }
  & .new-tag {
    margin-top: 1px;
    padding: 0.5rem 0.33rem;
    height: 1.25rem;
    color: ${tokens.color.text.default};
    background-color: ${tokens.color.accent.weak};
  }
`;
const useProductLines = ()=>{
    const t = useTranslate();
    const PRODUCT_LINES = {
        HAPPY_ASSET: {
            url: ()=>{
                return "";
            },
            new: false,
            name: t("nav:ExperienceSwitcher.product_asset"),
            description: t("nav:ExperienceSwitcher.product_asset_description")
        },
        HAPPY_PROPERTY: {
            url: ()=>{
                return "";
            },
            new: false,
            name: t("nav:ExperienceSwitcher.product_property"),
            description: t("nav:ExperienceSwitcher.product_property_description")
        },
        HAPPY_FORCE: {
            url: ()=>{
                return "";
            },
            new: true,
            name: t("nav:ExperienceSwitcher.product_force"),
            description: t("nav:ExperienceSwitcher.product_force_description")
        }
    };
    return PRODUCT_LINES;
};
const ProductLine = (props)=>{
    const productLines = useProductLines();
    const lineInfo = productLines[props.id];
    const [expanded, setExpanded] = useState(props.expanded);
    const t = useTranslate();
    return React.createElement(ProductLineWrapper, {
        className: props.id + (expanded ? " product-line-expanded" : ""),
        borderRadius: "large"
    }, React.createElement(Stack, {
        gap: 1.25
    }, React.createElement("label", {
        htmlFor: "Switcher-Experience-Expand-" + props.id
    }, React.createElement(Columns, {
        gap: 0.75,
        "aria-labelledby": "Switcher-Experience-Heading-" + props.id
    }, React.createElement(Column, {
        span: "content"
    }, React.createElement(ProductLineIconBox, null, props.id === "HAPPY_ASSET" && React.createElement(HappyAssetSVG, null), props.id === "HAPPY_PROPERTY" && React.createElement(HappyPropertySVG, null), props.id === "HAPPY_FORCE" && React.createElement(HappyForceSVG, null))), React.createElement(Column, {
        span: 1
    }, React.createElement(Inline, {
        alignY: "center"
    }, React.createElement(Stack, {
        gap: 0.5
    }, React.createElement(Inline, {
        alignY: "center",
        gap: 0.33
    }, React.createElement(Text, {
        weight: "bold",
        id: "Switcher-Experience-Heading-" + props.id
    }, lineInfo.name), !!lineInfo.new && React.createElement(Tag, {
        className: "new-tag"
    }, React.createElement(Text, {
        size: "small"
    }, t("nav:ExperienceSwitcher.new")))), React.createElement(Text, {
        size: "medium",
        weight: "bold",
        color: "slateGrey80"
    }, lineInfo.description)))), React.createElement(Column, {
        span: "content"
    }, React.createElement(Inline, {
        alignY: "center"
    }, !!props.available && React.createElement(ButtonIcon, {
        testId: "Switcher-ProductLine-Expand-" + props.id,
        className: "product-line-expand",
        id: "Switcher-Experience-Expand-" + props.id,
        icon: expanded ? "caret-up" : "caret-down",
        size: "xsmall",
        onClick: ()=>setExpanded(!expanded)
    }), !props.available && React.createElement(Box, {
        padding: [
            0,
            0.125,
            0,
            0
        ]
    }, React.createElement(Icon, {
        className: "link-icon",
        icon: "external-link",
        size: "xsmall"
    })))))), expanded && React.createElement(Stack, null, React.createElement(ProductList, {
        productLine: props.id
    }))));
};
const ProductList = ({ productLine  })=>{
    const uxs = useExperienceSwitcher();
    const t = useTranslate();
    const products = Object.keys(uxs.activeCustomer.productLines[productLine].products).sort((a, b)=>{
        const aKey = "nav:Product." + a;
        const bKey = "nav:Product." + b;
        return t(aKey).localeCompare(t(bKey));
    });
    return React.createElement(React.Fragment, null, products.map((name)=>{
        const product = uxs.activeCustomer.productLines[productLine].products[name];
        return React.createElement(Product, {
            key: name,
            name: name,
            productDefinition: product
        });
    }));
};
const ProductWrapper = styled(Box)`
  font-weight: ${tokens.font.weight.active};
  & .product-item {
    height: 2.25rem;
    padding-left: 2.75rem;
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
  }
  & .product-item:last-child {
    /* margin-bottom: -0.5rem; */
  }
  & label.product-businesses-expand {
    cursor: pointer;
    height: 2.25rem;
    margin-bottom: -0.5rem;
  }
  &.product-expanded label.product-businesses-expand {
    margin-bottom: 0;
  }
  /* Disable capsize on nested Texts to prevent alignment issues */
  & [data-hpy-web-text] {
    &::before,
    &::after {
      display: none;
    }
  }
`;
const ProductLink = styled.a`
  border-radius: ${tokens.size["border-radius"].sm};
  text-decoration: none;
  width: 100%;
  transition: background-color 0.5s ease;
  color: ${tokens.color.text.default};
  background-color: transparent;
  &:hover,
  &.product-active {
    background-color: ${tokens.color.accent.weak};
    color: ${tokens.color.text.default};
  }
  .beta-tag {
    transition: background-color 0.5s ease;
  }
  &:hover .beta-tag,
  &.product-active .beta-tag {
    background-color: ${tokens.color.palette.white[100]};
  }
  .product-active-icon {
    margin-right: 1rem;
  }
  .product-active-icon svg {
    padding: 0.125rem 0;
    stroke: ${tokens.color.text.default};
    stroke-width: 0.5;
  }
`;
const BetaTag = styled(Tag).attrs({
    className: "beta-tag"
})`
  color: ${NAV_BETA_COLOR_VAR};
  background-color: ${NAV_BETA_COLOR_BG_VAR};
  font-size: 0.625rem;
  letter-spacing: 1px;
  border-radius: ${tokens.size["border-radius"].circle};
  margin-top: -2px;
  height: 1.125rem;
`;
const TextTruncateFitContent = styled(Text).attrs({
    truncate: true,
    size: "medium"
})`
  width: unset;
  & > span {
    position: relative;
  }
`;
const ProductItem = ({ active , link , name , children , testId  })=>{
    return React.createElement(ProductLink, {
        className: "product-item" + (active ? " product-active" : ""),
        "data-test-id": "Product-" + testId,
        href: link
    }, React.createElement(Inline, {
        width: "fill",
        wrap: false,
        gap: 0.75,
        alignY: "center"
    }, React.createElement(TextTruncateFitContent, null, name), children, active && React.createElement(Icon, {
        className: "product-active-icon",
        icon: "check",
        size: 1
    })));
};
const Product = ({ name , productDefinition  })=>{
    const t = useTranslate();
    const productKey = "nav:Product." + name;
    const [expanded, setExpanded] = useState(productDefinition.productEnvironments.some((e)=>e.active));
    if (productDefinition.productEnvironments.length === 1) {
        return React.createElement(ProductWrapper, {
            width: "fill"
        }, React.createElement(ProductItem, {
            active: productDefinition.productEnvironments[0].active,
            link: productDefinition.productEnvironments[0].link,
            name: t(productKey),
            testId: name
        }, productDefinition.beta && React.createElement(BetaTag, null, t("nav:ExperienceSwitcher.beta"))));
    }
    const sortedProductEnvironments = [
        ...productDefinition.productEnvironments
    ].sort((a, b)=>a.name.localeCompare(b.name));
    return React.createElement(ProductWrapper, {
        width: "fill",
        className: expanded ? "product-expanded" : ""
    }, React.createElement(Stack, null, React.createElement("label", {
        className: "product-businesses-expand",
        htmlFor: "Switcher-Experience-Expand-Product-" + name
    }, React.createElement(Inline, {
        width: "fill",
        wrap: false,
        gap: 0.75,
        alignY: "center"
    }, React.createElement(TextTruncateFitContent, {
        className: "product-item"
    }, t(productKey)), productDefinition.beta && React.createElement(BetaTag, null, t("nav:ExperienceSwitcher.beta")), React.createElement(ButtonIcon, {
        testId: "Switcher-Experience-Expand-Product-" + name,
        id: "Switcher-Experience-Expand-Product-" + name,
        className: "product-expand",
        size: "xsmall",
        icon: expanded ? "caret-up" : "caret-down",
        onClick: ()=>setExpanded(!expanded)
    }))), expanded && sortedProductEnvironments.map((productEnvironment)=>{
        return React.createElement(ProductItem, {
            key: productEnvironment.businessId,
            active: productEnvironment.active,
            link: productEnvironment.link,
            name: productEnvironment.name,
            testId: productDefinition.name + "-" + productEnvironment.businessId
        });
    })));
};

export { ProductLine };
